<!-- How to play. Three illustrated panels, understandable without reading:
     switch the spark at a junction, light every bulb to switch on a home,
     keep going for the Merry Multiplier. -->
<template>
  <IonPage>
    <AppHeader title="How to play" back="/" />

    <IonContent :fullscreen="true">
      <div class="soc-stack howto-stack">
        <div class="howto-panel">
          <span class="step">Step 1</span>
          <svg viewBox="0 0 200 140" role="img" aria-label="Tap the glowing junction to send the spark down a different cable">
            <path d="M20 122 L100 70" class="cable-dim" />
            <path d="M100 70 L58 18" class="cable-dim" />
            <path d="M100 70 L172 30" class="cable-active" />
            <path d="M146 20 L172 30 L156 50" class="arrow" />
            <circle cx="100" cy="70" r="24" class="tap-ring" />
            <circle cx="100" cy="70" r="12" class="junction" />
          </svg>
          <p>Tap a junction to switch the spark</p>
        </div>

        <div class="howto-panel">
          <span class="step">Step 2</span>
          <svg viewBox="0 0 200 140" role="img" aria-label="Light every bulb on the cable to switch on the home behind it">
            <path d="M10 34 Q100 74 190 34" class="cable-dim" />
            <path d="M62 96 L62 60 L100 32 L138 60 L138 116 L62 116 Z" class="house" />
            <rect x="88" y="88" width="24" height="28" class="house-door" />
            <circle cx="38" cy="46" r="7" class="bulb bulb-1" />
            <circle cx="78" cy="60" r="7" class="bulb bulb-2" />
            <circle cx="122" cy="60" r="7" class="bulb bulb-3" />
            <circle cx="162" cy="46" r="7" class="bulb-off" />
          </svg>
          <p>Light every bulb to switch on a home</p>
        </div>

        <div class="howto-panel">
          <span class="step">Step 3</span>
          <svg viewBox="0 0 200 140" role="img" aria-label="Complete homes one after another to raise the Merry Multiplier">
            <path d="M60 92 L82 82" class="arrow-dim" />
            <path d="M122 66 L144 56" class="arrow-dim" />
            <circle cx="38" cy="100" r="22" class="chip chip-1" />
            <text x="38" y="107" font-size="18" class="chip-text">×1</text>
            <circle cx="100" cy="78" r="26" class="chip chip-2" />
            <text x="100" y="86" font-size="20" class="chip-text">×2</text>
            <circle cx="166" cy="50" r="30" class="chip chip-3" />
            <text x="166" y="58" font-size="22" class="chip-text chip-text--on-accent">×3</text>
          </svg>
          <p>Keep going to build your Merry Multiplier</p>
        </div>

        <IonButton class="soc-play" @click="play">Play</IonButton>
      </div>
    </IonContent>
  </IonPage>
</template>

<script setup>
import { onMounted } from 'vue';
import { IonPage, IonContent, IonButton } from '@ionic/vue';
import { useRouter } from 'vue-router';
import { AppHeader } from '../core/ui';
import { progress, persistProgress } from '../store.js';

const router = useRouter();

function play() {
  router.push({ name: 'play', params: { mode: 'free' } });
}

onMounted(() => {
  if (!progress.seenHowTo) {
    progress.seenHowTo = true;
    persistProgress();
  }
});
</script>

<style scoped>
.howto-stack {
  padding-top: var(--sp-4);
}

.howto-panel svg .cable-dim {
  fill: none;
  stroke: var(--soc-cable);
  stroke-width: 3;
  opacity: 0.6;
}

.howto-panel svg .cable-active {
  fill: none;
  stroke: var(--soc-cable-active);
  stroke-width: 4;
  stroke-linecap: round;
}

.howto-panel svg .arrow {
  fill: none;
  stroke: var(--soc-cable-active);
  stroke-width: 3;
  stroke-linecap: round;
  stroke-linejoin: round;
}

.howto-panel svg .arrow-dim {
  fill: none;
  stroke: var(--dim);
  stroke-width: 2.5;
  stroke-linecap: round;
  opacity: 0.6;
}

.howto-panel svg .tap-ring {
  fill: none;
  stroke: var(--accent);
  stroke-width: 3;
  stroke-dasharray: 4 6;
}

.howto-panel svg .junction {
  fill: var(--soc-junction-ring);
}

.howto-panel svg .house {
  fill: var(--soc-house-lit);
  stroke: var(--soc-roof-lit);
  stroke-width: 2;
  stroke-linejoin: round;
}

.howto-panel svg .house-door {
  fill: var(--soc-door);
}

.howto-panel svg .bulb-off {
  fill: var(--soc-bulb-off);
  stroke: var(--soc-cable);
  stroke-width: 1.5;
}

.howto-panel svg .bulb.bulb-1 {
  fill: var(--soc-bulb-1);
}

.howto-panel svg .bulb.bulb-2 {
  fill: var(--soc-bulb-2);
}

.howto-panel svg .bulb.bulb-3 {
  fill: var(--soc-bulb-3);
}

.howto-panel svg .chip {
  fill: var(--surface);
  stroke: var(--hairline);
  stroke-width: 2;
}

.howto-panel svg .chip-3 {
  fill: var(--accent);
  stroke: none;
}

.howto-panel svg .chip-text {
  fill: var(--ink);
  font-weight: 800;
  text-anchor: middle;
  font-family: inherit;
}

.howto-panel svg .chip-text--on-accent {
  fill: var(--accent-contrast);
}
</style>
